import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatAud } from "../../lib/api";
import { useCustomerAuth } from "../../context/CustomerAuthContext";
import StoreHeader from "../../components/layout/StoreHeader";
import StoreFooter from "../../components/layout/StoreFooter";
import { Package, Tag, Clock, Check, X, ChevronRight, ArrowRight, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

// ─── Counter offer Accept / Decline ───────────────────────────────────────
function CounterOfferActions({ offer, onUpdated }) {
  const [busy, setBusy] = useState(null);

  const accept = async () => {
    setBusy("accept");
    try {
      await api.post(`/offers/${offer.id}/accept-counter`);
      toast.success(`Counter accepted at ${formatAud(offer.counter_price)} — visit the product to buy`);
      onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to accept counter");
    } finally { setBusy(null); }
  };

  const decline = async () => {
    setBusy("decline");
    try {
      await api.post(`/offers/${offer.id}/decline-counter`);
      toast.success("Counter offer declined");
      onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to decline");
    } finally { setBusy(null); }
  };

  return (
    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-sm space-y-3">
      <div className="text-[11px] font-mono uppercase tracking-widest text-blue-600">BCP Counter Offer</div>
      <div className="flex items-center gap-3">
        <span className="font-heading font-black text-[22px] text-[#1e5bb8]">{formatAud(offer.counter_price)}</span>
        <div>
          <div className="text-[11px] text-zinc-400 font-mono line-through">{formatAud(offer.list_price)}</div>
          <div className="text-[11px] text-green-600 font-mono font-bold">
            Save {formatAud(offer.list_price - offer.counter_price)}
          </div>
        </div>
      </div>
      {offer.decision_message && (
        <p className="text-[13px] text-blue-700 italic">"{offer.decision_message}"</p>
      )}
      {offer.expires_at && (
        <div className="flex items-center gap-1.5 text-[11px] font-mono text-amber-600 font-bold">
          <Clock className="h-3 w-3" /> Expires: <OfferCountdown expiresAt={offer.expires_at} />
        </div>
      )}
      <div className="flex gap-2 pt-1">
        <button onClick={accept} disabled={!!busy}
          className="flex-1 h-9 bg-[#1e5bb8] hover:bg-[#0b3ea0] text-white font-bold uppercase tracking-wide text-[12px] rounded-sm transition-colors flex items-center justify-center gap-1.5">
          <Check className="h-3.5 w-3.5" />
          {busy === "accept" ? "Accepting…" : `Accept ${formatAud(offer.counter_price)}`}
        </button>
        <button onClick={decline} disabled={!!busy}
          className="h-9 px-4 border border-zinc-300 hover:border-zinc-400 text-zinc-600 font-bold uppercase tracking-wide text-[12px] rounded-sm transition-colors flex items-center gap-1.5">
          <X className="h-3.5 w-3.5" />
          {busy === "decline" ? "…" : "Decline"}
        </button>
      </div>
    </div>
  );
}

// ─── Live offer countdown ─────────────────────────────────────────────────
function OfferCountdown({ expiresAt }) {
  const [txt, setTxt] = useState("");
  const [urgent, setUrgent] = useState(false);

  useEffect(() => {
    const tick = () => {
      const diff = new Date(expiresAt) - Date.now();
      if (diff <= 0) { setTxt("Expired"); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setUrgent(diff < 3600000);
      setTxt(`${h}h ${m.toString().padStart(2,"0")}m ${s.toString().padStart(2,"0")}s`);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [expiresAt]);

  if (txt === "Expired") return (
    <span className="text-[11px] font-mono text-zinc-400 uppercase tracking-widest">Expired</span>
  );
  return (
    <span className={`inline-flex items-center gap-1 text-[12px] font-mono font-bold ${urgent ? "text-[#e31e24]" : "text-[#1e5bb8]"}`}>
      <Clock className={`h-3.5 w-3.5 ${urgent ? "animate-pulse" : ""}`} />
      {txt}
    </span>
  );
}

const STATUS_STYLE = {
  pending:   { color: "text-amber-600 bg-amber-50 border-amber-200",   label: "Pending review"  },
  accepted:  { color: "text-green-700 bg-green-50 border-green-200",   label: "Accepted ✓"      },
  rejected:  { color: "text-red-700 bg-red-50 border-red-200",         label: "Declined"        },
  countered: { color: "text-blue-700 bg-blue-50 border-blue-200",      label: "Counter offered" },
  expired:   { color: "text-zinc-500 bg-zinc-50 border-zinc-200",      label: "Expired"         },
};

const ORDER_STATUS_COLOR = {
  new:       "text-blue-600",
  confirmed: "text-blue-700",
  packed:    "text-purple-600",
  shipped:   "text-amber-600",
  delivered: "text-green-600",
  cancelled: "text-red-500",
};

export default function Account() {
  const { user, loading, loginWithGoogle, logout } = useCustomerAuth();
  const [tab,    setTab]    = useState("orders");
  const [orders, setOrders] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loadingData, setLoadingData] = useState(false);

  useEffect(() => {
    if (!user) return;
    setLoadingData(true);
    Promise.all([
      api.get("/orders/mine").catch(() => ({ data: [] })),
      api.get(`/offers/mine`).catch(() => ({ data: { items: [] } })),
    ]).then(([ordRes, offRes]) => {
      setOrders(ordRes.data || []);
      setOffers(offRes.data.items || []);
    }).finally(() => setLoadingData(false));
  }, [user]);

  if (loading) return (
    <div className="bg-white min-h-screen">
      <StoreHeader />
      <div className="max-w-4xl mx-auto px-4 py-20 text-center font-mono uppercase tracking-widest text-zinc-500 text-xs">Loading…</div>
    </div>
  );

  if (!user) return (
    <div className="bg-white min-h-screen" data-testid="account-signin-required">
      <StoreHeader />
      <div className="max-w-xl mx-auto px-4 md:px-8 py-20 text-center">
        <div className="text-[#e31e24] font-mono text-xs uppercase tracking-[0.3em] mb-2">Account</div>
        <h1 className="font-heading text-3xl font-black uppercase tracking-tight text-[#0b1a3b]">Sign in to continue</h1>
        <p className="mt-3 text-zinc-600 text-[15px]">Access your orders, offers and account history.</p>
        <button onClick={loginWithGoogle}
          className="mt-8 h-12 px-8 inline-flex items-center gap-3 font-heading font-black uppercase tracking-wide text-[14px] bg-[#0b1a3b] hover:bg-[#1e5bb8] text-white transition-colors"
          data-testid="account-signin-google">
          Sign in with Google
        </button>
      </div>
      <StoreFooter />
    </div>
  );

  const pendingOffers = offers.filter((o) => o.status === "pending").length;
  const acceptedOffers = offers.filter((o) => o.status === "accepted").length;

  return (
    <div className="bg-white min-h-screen" data-testid="account-page">
      <StoreHeader />

      <div className="max-w-5xl mx-auto px-4 md:px-8 py-10">

        {/* Profile header */}
        <div className="flex items-center gap-5 pb-8 border-b border-zinc-200 mb-8">
          {user.picture
            ? <img src={user.picture} alt="" className="w-16 h-16 rounded-sm object-cover" />
            : <div className="w-16 h-16 rounded-sm bg-[#0b1a3b] flex items-center justify-center text-white font-heading font-black text-2xl">
                {(user.name || "?")[0].toUpperCase()}
              </div>
          }
          <div className="flex-1">
            <h1 className="font-heading text-2xl font-black uppercase text-[#0b1a3b]">{user.name}</h1>
            <div className="text-zinc-500 text-[14px]">{user.email}</div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Link to="/shop"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#e31e24] hover:bg-[#b71820] text-white font-heading font-bold uppercase tracking-wide text-[13px] transition-colors">
              Shop Parts <ArrowRight className="h-3.5 w-3.5" />
            </Link>
            <button onClick={logout}
              className="px-4 py-2.5 border border-zinc-300 hover:border-zinc-400 text-zinc-600 font-heading font-bold uppercase tracking-wide text-[13px] transition-colors">
              Sign out
            </button>
          </div>
        </div>

        {/* Quick stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          {[
            { label: "Orders",          value: orders.length,   color: "text-[#0b1a3b]" },
            { label: "Active Offers",   value: pendingOffers,   color: pendingOffers > 0 ? "text-amber-600" : "text-[#0b1a3b]" },
            { label: "Accepted Offers", value: acceptedOffers,  color: acceptedOffers > 0 ? "text-green-600" : "text-[#0b1a3b]" },
          ].map(({ label, value, color }) => (
            <div key={label} className="border border-zinc-200 rounded-sm p-5 text-center">
              <div className={`font-heading text-3xl font-black ${color}`}>{value}</div>
              <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 mt-1">{label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-0 border-b border-zinc-200 mb-6">
          {[
            { key: "orders", label: "My Orders",     badge: orders.length },
            { key: "offers", label: "My Offers",     badge: pendingOffers > 0 ? pendingOffers : null },
          ].map(({ key, label, badge }) => (
            <button key={key} onClick={() => setTab(key)}
              className={`px-5 py-3 text-[13px] font-heading font-bold uppercase tracking-wide border-b-2 transition-colors flex items-center gap-2 ${
                tab === key ? "border-[#e31e24] text-[#0b1a3b]" : "border-transparent text-zinc-400 hover:text-[#0b1a3b]"
              }`}>
              {label}
              {badge != null && (
                <span className={`min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold flex items-center justify-center ${
                  tab === key ? "bg-[#e31e24] text-white" : "bg-zinc-200 text-zinc-600"
                }`}>{badge}</span>
              )}
            </button>
          ))}
        </div>

        {/* ── Orders tab ── */}
        {tab === "orders" && (
          <div>
            {loadingData ? (
              <div className="text-center py-12 text-zinc-400 font-mono text-xs uppercase tracking-widest">Loading orders…</div>
            ) : orders.length === 0 ? (
              <div className="text-center py-16 border border-dashed border-zinc-200 rounded-sm">
                <Package className="h-12 w-12 mx-auto mb-4 text-zinc-200" />
                <div className="font-heading font-bold text-lg uppercase text-zinc-300">No orders yet</div>
                <Link to="/shop" className="mt-4 inline-flex items-center gap-2 text-[#1e5bb8] font-bold uppercase text-sm hover:text-[#0b3ea0]">
                  Browse Parts <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            ) : (
              <div className="space-y-3" data-testid="account-orders-list">
                {orders.map((o) => (
                  <div key={o.id} className="border border-zinc-200 rounded-sm p-4 hover:border-[#1e5bb8] transition-colors">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div>
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="font-mono font-bold text-[#0b1a3b] text-[14px]">{o.order_number}</span>
                          <span className={`text-[11px] font-mono uppercase tracking-widest font-bold ${ORDER_STATUS_COLOR[o.status] || "text-zinc-500"}`}>
                            {o.status}
                          </span>
                          {o.backorder_status === "pending_stock" && (
                            <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-sm">
                              Backorder
                            </span>
                          )}
                        </div>
                        <div className="text-[12px] text-zinc-400 mt-1 font-mono">
                          {new Date(o.created_at).toLocaleDateString("en-AU", { day:"2-digit", month:"short", year:"numeric" })}
                          {" · "}
                          {o.items?.length || 0} item{o.items?.length !== 1 ? "s" : ""}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-heading font-black text-[#0b1a3b] text-[18px]">{formatAud(o.total)}</div>
                        <div className={`text-[11px] font-mono uppercase tracking-widest ${o.payment_status === "paid" ? "text-green-600" : "text-amber-600"}`}>
                          {o.payment_status}
                        </div>
                      </div>
                    </div>
                    {o.items?.slice(0, 2).map((item, i) => (
                      <div key={i} className="mt-3 flex items-center gap-3 text-[13px] text-zinc-600 border-t border-zinc-100 pt-3">
                        {item.image_url && <img src={item.image_url} alt="" className="w-9 h-9 object-cover rounded-sm bg-zinc-100" />}
                        <span className="flex-1 truncate">{item.name}</span>
                        <span className="font-mono text-zinc-500 shrink-0">×{item.qty}</span>
                        <span className="font-medium text-[#0b1a3b] shrink-0">{formatAud(item.price)}</span>
                      </div>
                    ))}
                    {(o.items?.length || 0) > 2 && (
                      <div className="text-[11px] text-zinc-400 mt-2 font-mono">+{o.items.length - 2} more item{o.items.length - 2 !== 1 ? "s" : ""}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Offers tab ── */}
        {tab === "offers" && (
          <div>
            {offers.length === 0 ? (
              <div className="text-center py-16 border border-dashed border-zinc-200 rounded-sm">
                <Tag className="h-12 w-12 mx-auto mb-4 text-zinc-200" />
                <div className="font-heading font-bold text-lg uppercase text-zinc-300">No offers yet</div>
                <p className="text-[14px] text-zinc-400 mt-2 mb-4">Found a part? Click "Make Offer" on any product page to submit a price offer.</p>
                <Link to="/shop" className="inline-flex items-center gap-2 text-[#1e5bb8] font-bold uppercase text-sm hover:text-[#0b3ea0]">
                  Browse Parts <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {offers.map((o) => {
                  const st = STATUS_STYLE[o.status] || STATUS_STYLE.expired;
                  const isPending = o.status === "pending";
                  return (
                    <div key={o.id} className={`border rounded-sm p-5 ${isPending ? "border-amber-200 bg-amber-50/30" : "border-zinc-200"}`}>
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 flex-wrap mb-1">
                            <span className="font-mono font-bold text-[#0b1a3b] text-[13px]">{o.offer_number}</span>
                            <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border rounded-sm ${st.color}`}>
                              {st.label}
                            </span>
                          </div>
                          <div className="font-heading font-bold text-[#0b1a3b] text-[16px]">{o.product_name}</div>
                          <div className="text-[12px] text-zinc-400 font-mono mt-0.5">SKU: {o.product_sku}</div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-[11px] font-mono text-zinc-400 uppercase tracking-widest">Your offer</div>
                          <div className="font-heading font-black text-[20px] text-[#0b1a3b]">{formatAud(o.offered_price)}</div>
                          <div className="text-[11px] text-zinc-400 font-mono">List: {formatAud(o.list_price)}</div>
                        </div>
                      </div>

                      {/* Counter offer — Accept / Decline buttons */}
                      {o.status === "countered" && o.counter_price && (
                        <CounterOfferActions offer={o} onUpdated={() => {
                          api.get(`/offers/mine`).then(r => setOffers(r.data.items || []));
                        }} />
                      )}

                      {/* Accepted offer — Buy at negotiated price */}
                      {o.status === "accepted" && (
                        <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-sm">
                          <div className="flex items-start gap-2 mb-2">
                            <Check className="h-4 w-4 text-green-600 shrink-0 mt-0.5" />
                            <div className="text-[13px] text-green-800 font-semibold">
                              Accepted at {formatAud(o.offered_price)}
                            </div>
                          </div>
                          <p className="text-[12px] text-green-700 mb-3">
                            Your price is active — sign in and visit the product page to buy at your negotiated price.
                          </p>
                          <Link
                            to={`/product/${o.product_id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-700 text-white text-[12px] font-bold uppercase tracking-wide rounded-sm hover:bg-green-800 transition-colors">
                            🛒 Buy at {formatAud(o.offered_price)}
                          </Link>
                        </div>
                      )}

                      {/* Countdown for pending offers */}
                      <div className="mt-3 pt-3 border-t border-zinc-200 flex items-center justify-between flex-wrap gap-2">
                        <div className="text-[12px] font-mono text-zinc-400">
                          Submitted {new Date(o.created_at).toLocaleDateString("en-AU")}
                        </div>
                        {isPending && o.expires_at && (
                          <div className="flex items-center gap-2">
                            <span className="text-[11px] text-zinc-500 font-mono uppercase tracking-widest">Expires in:</span>
                            <OfferCountdown expiresAt={o.expires_at} />
                          </div>
                        )}
                        {o.status === "expired" && (
                          <span className="text-[11px] font-mono text-zinc-400 flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" /> This offer expired — browse parts to make a new offer
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
      <StoreFooter />
    </div>
  );
}